import { ActionButton, getId, useTheme } from '@fluentui/react';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';

import React, { useCallback, useEffect, useState } from 'react';
import { CredentialDialog } from './CredentialDialog';
import { useEnableVerifyId, useRetrievingCredential } from './useRetrievingCredential';
import { ICredentialError } from './Credential.type';
import { useTelemetry } from '@iamexperiences/ecos-telemetry';
import { CopyLinkDialog } from './CopyLinkDialog';
import { IUserPerson } from '../models/IUserPerson';
import { useGetUser } from '../../hooks/useGetUser';
import { useGetUserPhoto } from '../../hooks/useGetUserPhoto';

const accountStyles = require('./../PersonBadge/PersonBadge.scoped.scss');

interface IGetVerifyIdProps {
  userPerson: IUserPerson;
}

export const GetVerifyId: React.FC<IGetVerifyIdProps> = ({ userPerson }) => {
  const [t] = useLocalization('Account');
  const theme = useTheme();
  const telemetry = useTelemetry();
  const enabledVerifyIdQuery = useEnableVerifyId();
  const getUserPersonQuery = useGetUser();
  const retrievingCredentialMutation = useRetrievingCredential();
  const { data: userPhotoUrl } = useGetUserPhoto();

  const [showingQrDialog, setShowingQrDialog] = useState(false);
  const [showingCopyDialog, setShowingCopyDialog] = useState(false);

  const textColor = theme.palette.themePrimary;
  const actionButtonId = getId();

  useEffect(() => {
    if (retrievingCredentialMutation.error) {
      telemetry.error('getCredential', retrievingCredentialMutation.error);
    }
    if (enabledVerifyIdQuery.error) {
      telemetry.error('enableVerifyId', enabledVerifyIdQuery.error);
    }
  }, [enabledVerifyIdQuery.error, retrievingCredentialMutation.error, telemetry]);

  const showDialog = useCallback((showQrDialog = false, showCopyDialog = false) => {
    setShowingQrDialog(showQrDialog);
    setShowingCopyDialog(showCopyDialog);
  }, []);

  const generateQrCode = useCallback(() => {
    retrievingCredentialMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getUserPersonQuery.data?.givenName,
    getUserPersonQuery.data?.surname,
    userPhotoUrl,
    userPerson.displayName,
    userPerson.jobTitle,
    userPerson.mail,
    userPerson.preferredLanguage,
    userPerson.userPrincipalName,
  ]);

  const getVerifyIdClick = useCallback(() => {
    showDialog(true);
    generateQrCode();
  }, [generateQrCode, showDialog]);

  return enabledVerifyIdQuery.data === true ? (
    <>
      <hr className={accountStyles.horizontalLine} />
      <ActionButton
        id={actionButtonId}
        onClick={getVerifyIdClick}
        styles={{
          root: {
            color: textColor,
            marginTop: '20px',
          },
          labelHovered: {
            textDecoration: 'underline',
          },
        }}
      >
        {t('getMyVerifiedId')}
      </ActionButton>

      {showingQrDialog && (
        <CredentialDialog
          onDone={() => showDialog()}
          isLoading={retrievingCredentialMutation.isLoading}
          error={retrievingCredentialMutation.error as ICredentialError}
          onGenerateNewOne={generateQrCode}
          onCopyLink={() => showDialog(false, true)}
          credential={retrievingCredentialMutation.data}
        />
      )}
      {retrievingCredentialMutation.data && showingCopyDialog && (
        <CopyLinkDialog
          onBack={() => showDialog(true)}
          onDone={() => showDialog(false)}
          link={retrievingCredentialMutation.data.url}
        />
      )}
    </>
  ) : null;
};
